import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { getCloudinaryUrl, Image } from '../../../Image';
import { IMAGE_SIZE } from '../../IngredientListItem.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientListItemImage = memo((props: Props) => {
  const { url, alt, imageWidth, isSelected } = props;

  const formattedSource = getCloudinaryUrl(url, { crop: 'scale', width: imageWidth }); // prettier-ignore

  // ─── Animated Styles ─────────────────────────────────────────────────

  const highlightOpacitySV = useDerivedValue(() => {
    return withTiming(isSelected ? 1 : 0, {
      duration: theme.transitions.short,
      easing: Easing.inOut(Easing.ease),
    });
  });

  const highlightRingStyles = useAnimatedStyle(() => {
    return { opacity: highlightOpacitySV.value };
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <Image
        source={formattedSource}
        style={[styles.image]}
        alt={alt}
        pointerEvents="none" // NOTE: This is to prevent the image from being selected/dragged.
      />
      <Animated.View style={[styles.highlightRing, highlightRingStyles]} />
    </View>
  );
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: IMAGE_SIZE,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  image: {
    width: '100%',
    height: '100%',
    borderRadius: IMAGE_SIZE,
  },
  highlightRing: {
    ...StyleSheet.absoluteFillObject,
    margin: -1, // NOTE: To cover whole image
    borderRadius: IMAGE_SIZE,
    borderWidth: 5,
    borderColor: theme.colors.LIME,
    opacity: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  url: string;
  alt: string;
  imageWidth: number;
  isSelected?: boolean;
};
